import { saveUpdateContent } from '../../../../../../context-controller-mod/context-serv/global-provider/local-storage-context/updateContentStorage';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../css/contentSectionEditPage.css';
import Cookies from 'js-cookie';
import ApiGet    from '../../../../../../api-controller-mod/api-serv/get-api/getApi';
import ApiPut    from '../../../../../../api-controller-mod/api-serv/put-api/putApi';
import ApiDelete from '../../../../../../api-controller-mod/api-serv/delete-api/deleteApi';
import { API_ENDPOINTS } from '../../../../../../context-controller-mod/context-serv/global-provider/api-shell/api-provider';
import { getSectionSchema, getByKey, setByKey } from './sectionSchema';
import TextField   from '../../../../../../component-controller-mod/component-serv/input-fs-cp/text-field-op/ts/textField';
import SelectField, { SelectValue } from '../../../../../../component-controller-mod/component-serv/input-fs-cp/select-field-op/ts/selectField';
import { Button, Confirmation, Toggle } from '@core/uilibrary';
import EditContentBreadcrumb from '../../../../../../component-controller-mod/component-serv/navigation-fs-cp/breadcrumb-nav-op/edit-content-breadcrumb/ts/editContentBreadcrumb';
import { useGlobalPopupProvider } from '../../../../../../context-controller-mod/context-serv/popup-provider/ts/globalPopupProvider';
import { useToast } from '../../../../../../context-controller-mod/context-serv/toast-provider/bottomToast/ts/ToastProvider';
import { WarningConfirmationIcon } from '../../../../../../asset-controller-mod/asset-serv/icons/iconExporter';

import { ApiSection, ListItem, IconItem, SlugResponse, UpdateEntry } from './types';
import { STATUS_OPTIONS, toApiSlug } from './constants';
import FieldRenderer from './fieldRenderer';

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

const ContentSectionEditPage: React.FC = () => {
  const { contentType, sectionId, contentUuid: paramContentUuid, contentVersionUuid: paramContentVersionUuid } = useParams<{ contentType: string; sectionId: string; contentUuid: string; contentVersionUuid: string }>();
  const navigate = useNavigate();
  const { showToast }   = useToast();
  const { openConfirm, closeConfirm } = useGlobalPopupProvider();

  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [sectionRaw,  setSectionRaw]  = useState<ApiSection | null>(null);
  const [sectionKey,  setSectionKey]  = useState('');
  const [contentUuid, setContentUuid] = useState(paramContentUuid ?? '');
  const [metaSection, setMetaSection] = useState('');
  const [metaStatus,  setMetaStatus]  = useState('PUBLISHED');
  const [sectionVisible, setSectionVisible] = useState(true);

  const [editValues,         setEditValues]         = useState<Record<string, unknown>>({});
  const [toggles,            setToggles]            = useState<Record<string, boolean>>({});
  const [lists,              setLists]              = useState<ListItem[]>([]);
  const [selectedIdx,        setSelectedIdx]        = useState(0);
  const [cardVisible,        setCardVisible]        = useState(true);
  const [listItemEditValues, setListItemEditValues] = useState<Record<string, unknown>>({});

  const userId             = Cookies.get('userUuid')           ?? '';
  const contentVersionUuid = paramContentVersionUuid ?? Cookies.get('contentVersionUuid') ?? '';

  const isSavedRef   = useRef(false);
  const origStatus   = useRef<string>('PUBLISHED');
  const origTitle   = useRef<string>('');
  const origAuthor  = useRef<string>('');
  const origSection = useRef<ApiSection | null>(null);

  const apiSlug = toApiSlug(contentType ?? '');

  // ── Fetch by slug ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!contentType || !sectionId) return;
    if (!contentVersionUuid) {
      console.warn('[SectionEdit] no contentVersionUuid cookie found');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    ApiGet<SlugResponse>(
      `${API_ENDPOINTS.GET_CMS_CONTENT}?slug=${apiSlug}`
    )
      .then(res => {
        if (cancelled) return;
        const resData = res?.data?.data;
        if (!resData) return;

        setContentUuid(resData.contentId);
        setMetaStatus(resData.status ?? 'PUBLISHED');
        origStatus.current = resData.status ?? 'PUBLISHED';

        const ctaArr = resData.data?.[apiSlug]?.cta ?? [];
        const cta    = ctaArr[0];
        if (!cta) return;

        // Resolve sectionKey — direct key or 1-based index fallback
        let key = sectionId;
        let sec = cta[key];
        if (!sec) {
          const allKeys = Object.keys(cta);
          const idx     = parseInt(sectionId, 10) - 1;
          key = allKeys[idx] ?? '';
          sec = key ? cta[key] : undefined;
        }
        if (!sec) return;
        const resolvedSec = sec as ApiSection;

        setSectionKey(key);
        setSectionRaw(resolvedSec);
        setSectionVisible(resolvedSec.visible ?? true);
        setMetaSection(key.replace(/Section$/i, '').replace(/^\w/, c => c.toUpperCase()));
        setEditValues(resolvedSec as unknown as Record<string, unknown>);

        origSection.current = resolvedSec;
        origTitle.current   = resolvedSec.title  ?? '';
        origAuthor.current  = resolvedSec.author ?? '';

        const l = resolvedSec.lists ?? [];
        setLists(l);
        if (l.length > 0) setListItemEditValues(l[0] as unknown as Record<string, unknown>);
      })
      .catch(err => { if (!cancelled) console.error('[SectionEdit] fetch:', err); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [contentType, sectionId, apiSlug]);

  // ── Delete draft ───────────────────────────────────────────────────────────
  const deleteDraft = useCallback(async () => {
    if (!contentVersionUuid) return;
    try {
      await ApiDelete(
        `${API_ENDPOINTS.DELETE_DRAFT_CONTENT}?version=${contentVersionUuid}`
      );
    } catch (err) {
      console.warn('[SectionEdit] delete draft failed:', err);
    } finally {
      Cookies.remove('contentVersionUuid');
    }
  }, [contentVersionUuid]);

  useEffect(() => {
    return () => { if (!isSavedRef.current) deleteDraft(); };
  }, [deleteDraft]);

  // ── Build path-based updates ───────────────────────────────────────────────
  const buildUpdates = useCallback((nextStatus: string): UpdateEntry[] => {
    const updates:      UpdateEntry[] = [];
    const fieldUpdates: UpdateEntry[] = [];
    if (!origSection.current) return updates;

    const schema = getSectionSchema(apiSlug, sectionKey);
    const BASE   = [apiSlug, 'cta', 0, sectionKey] as const;

    // Section-level visible
    if (sectionVisible !== origSection.current.visible)
      fieldUpdates.push({ path: [...BASE, 'visible'], value: sectionVisible });

    if (schema) {
      for (const field of schema.fields) {
        if (field.type === 'list') {
          const listsChanged = lists.some((item, idx) => {
            const orig = origSection.current!.lists?.[idx];
            if (!orig) return true;
            return field.listFields.some(sub =>
              getByKey(item as unknown as Record<string, unknown>, sub.key) !==
              getByKey(orig  as unknown as Record<string, unknown>, sub.key)
            );
          });
          if (listsChanged)
            fieldUpdates.push({ path: [...BASE, 'lists'], value: lists });

        } else if (field.type !== 'image') {
          const curr = getByKey(editValues,                                     field.key);
          const orig = getByKey(origSection.current as Record<string, unknown>, field.key);
          if (curr !== undefined && curr !== orig)
            fieldUpdates.push({
              path:  [...BASE, ...field.key.split('.')],
              value: curr as string | boolean,
            });
        }
      }
    }

    // Mark DRAFT if any field changed; override if user chose a different status
    if (fieldUpdates.length > 0) {
      updates.push(...fieldUpdates);
      updates.push({ path: [...BASE, 'status'], value: 'DRAFT' });
      if (nextStatus !== origStatus.current)
        updates.push({ path: [...BASE, 'status'], value: nextStatus });
    }

    return updates;
  }, [apiSlug, sectionKey, editValues, lists, sectionVisible]);

  // ── Save ───────────────────────────────────────────────────────────────────
  const performSave = useCallback(async () => {
    setSaving(true);
    try {
      await ApiPut(
        `${API_ENDPOINTS.UPDATE_CONTENT_BY_PATH}?contentUuid=${contentUuid}&userId=${userId}&versionUuid=${contentVersionUuid}`,
        { type: 'page', title: origTitle.current, version: 1, updates: buildUpdates(metaStatus) },
      );
      isSavedRef.current = true;
      saveUpdateContent(contentType ?? apiSlug, contentType ?? apiSlug, contentUuid, contentVersionUuid, sectionId ?? sectionKey);
      Cookies.remove('contentVersionUuid');
      showToast({ status: 'success', title: 'Complete !',   description: 'Section was updated successfully.' });
      navigate(`/content/${contentType}`);
    } catch (err) {
      console.error('[SectionEdit] save:', err);
      showToast({ status: 'error',   title: 'Uncomplete !', description: 'Section was updated unsuccessfully.' });
    } finally {
      setSaving(false);
    }
  }, [buildUpdates, contentUuid, userId, contentVersionUuid, contentType, navigate, showToast]);

  // ── Confirm save ───────────────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    openConfirm(
      <Confirmation visible title="Save changes"
        description={`Are you sure you want to save changes to the "${metaSection}" section? This will update the live content.`}
        status="warning" icon={<WarningConfirmationIcon />} onClose={closeConfirm}
        buttons={<>
          <Button variant="ghost"   size="md" value="Cancel" type="button" onClick={closeConfirm} />
          <Button variant="primary" size="md" value="Save"   type="button"
            onClick={() => { closeConfirm(); performSave(); }} />
        </>}
      />
    );
  }, [openConfirm, closeConfirm, metaSection, performSave]);

  // ── Confirm cancel ─────────────────────────────────────────────────────────
  const handleCancel = useCallback(() => {
    openConfirm(
      <Confirmation visible title="Discard changes"
        description="Are you sure you want to discard all unsaved changes? This action cannot be undone."
        status="warning" icon={<WarningConfirmationIcon />} onClose={closeConfirm}
        buttons={<>
          <Button variant="ghost"  size="md" value="Stay"    type="button" onClick={closeConfirm} />
          <Button variant="danger" size="md" value="Discard" type="button"
            onClick={async () => {
              closeConfirm();
              await deleteDraft();
              isSavedRef.current = true;
              navigate(`/content/${contentType}`);
            }} />
        </>}
      />
    );
  }, [openConfirm, closeConfirm, navigate, contentType, deleteDraft]);

  // ── Field change handlers ──────────────────────────────────────────────────
  const handleFieldChange = useCallback((key: string, v: unknown) =>
    setEditValues(prev => setByKey(prev, key, v)), []);

  const handleToggle = useCallback((key: string, v: boolean) =>
    setToggles(prev => ({ ...prev, [key]: v })), []);

  const handleCardSelect = useCallback((idx: number) => {
    setSelectedIdx(idx);
    setListItemEditValues(lists[idx] as unknown as Record<string, unknown> ?? {});
  }, [lists]);

  const handleListItemChange = useCallback((key: string, v: unknown) => {
    setListItemEditValues(prev => {
      const parts = key.split('.');
      if (parts[0] === 'items' && parts.length === 3) {
        const j     = parseInt(parts[1], 10);
        const field = parts[2];
        const items = (prev.items as IconItem[] | undefined) ?? [];
        return { ...prev, items: items.map((ic, k) => k === j ? { ...ic, [field]: v } : ic) };
      }
      return setByKey(prev, key, v);
    });
  }, []);

  // ── Derive schema & page metadata ──────────────────────────────────────────
  const schema    = getSectionSchema(apiSlug, sectionKey || sectionId || '');
  const pageLabel = contentType
    ? contentType.charAt(0).toUpperCase() + contentType.slice(1).replace(/-/g, ' ')
    : 'Content';

  const showSectionHeading = schema != null && (
    schema.alwaysShowHeading ||
    (sectionRaw != null && sectionRaw.label != null && sectionRaw.label !== '')
  );

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="cse-root">
        <div className="cse-skeleton-wrap">
          <div className="cse-skeleton cse-skeleton--title" />
          <div className="cse-skeleton cse-skeleton--desc"  />
          <div className="cse-skeleton cse-skeleton--field" />
          <div className="cse-skeleton cse-skeleton--field" />
          <div className="cse-skeleton cse-skeleton--field" />
        </div>
      </div>
    );
  }

  if (!sectionRaw) {
    return (
      <div className="cse-root">
        <div className="cse-empty-state">
          <p className="cse-empty-title">Section not found</p>
          <p className="cse-empty-desc">Could not locate "{sectionId}" in "{contentType}".</p>
          <Button variant="ghost" value="← Back" onClick={() => navigate(`/content/${contentType}`)} />
        </div>
      </div>
    );
  }

  if (!schema) {
    return (
      <div className="cse-root">
        <div className="cse-empty-state">
          <p className="cse-empty-title">No schema defined</p>
          <p className="cse-empty-desc">Schema not yet configured for "{sectionKey}" in "{contentType}".</p>
          <Button variant="ghost" value="← Back" onClick={() => navigate(`/content/${contentType}`)} />
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="cse-root">

      <EditContentBreadcrumb
        onHomeClick={() => navigate('/overview')}
        onContentClick={() => navigate(`/content/${contentType}`)}
      />

      <h1 className="cse-page-title">Edit section</h1>
      <p className="cse-page-desc">
        Responsible for managing and maintaining website content to ensure accuracy.
      </p>

      <div className="cse-meta-grid">
        <TextField label="Section ID" value={sectionId ?? ''}    disabled />
        <TextField label="Author"     value={origAuthor.current} disabled />
        <TextField label="Section"    value={metaSection}        disabled />
        <TextField label="Title"      value={origTitle.current}  disabled />
      </div>

      <div className="cse-section-editor">
        {showSectionHeading && (
          <>
            <div className="cse-section-heading-row">
              <h2 className="cse-section-heading">{pageLabel}-{schema.label} sector</h2>
              <Toggle checked={sectionVisible} onChange={setSectionVisible} />
            </div>
            <div className="cse-divider" />
          </>
        )}

        {schema.fields.map(field => (
          <FieldRenderer
            key={field.key}
            field={field}
            sectionData={sectionRaw as unknown as Record<string, unknown>}
            editValues={editValues}
            onChange={handleFieldChange}
            toggles={toggles}
            onToggle={handleToggle}
            lists={lists}
            selectedIdx={selectedIdx}
            cardVisible={cardVisible}
            onCardVisible={setCardVisible}
            onCardSelect={handleCardSelect}
            onListItemChange={handleListItemChange}
            listItemEditValues={listItemEditValues}
            pageLabel={pageLabel}
          />
        ))}
      </div>

      <div className="cse-actions">
        <Button variant="ghost"   value="Cancel"                      onClick={handleCancel} />
        <Button variant="primary" value={saving ? 'Saving…' : 'Save'} onClick={handleSave}  disabled={saving} />
      </div>

    </div>
  );
};

export default ContentSectionEditPage;